/**
 * The by-sheet price-book row as the host seeds it (CRM `mapPricebookSheetPreviewRows`). Ids follow
 * `<sheetId>:hourly | :mobilisation | :travel | :yardage | :crew:<id>[:travel] | :item:<id>`.
 *
 * The parse is defensive: rows arrive through `DocumentContext.priceBook` as untyped records, so
 * every field is narrowed here and anything unexpected falls back rather than throwing. Cost fields
 * (`cost`, `costRate`, `margin`) are never read, so they can never reach a rendered cell.
 */
import { toMoneyAmount } from '@/common/domain/money/toMoneyAmount';

export type PriceBookRowKind = 'equipment' | 'crew' | 'lineItem';

export type PriceBookSubsectionKey = 'equipment' | 'labor' | 'material' | 'additional';

export interface PriceBookBuildRow {
  id: string;
  sheetId: string;
  sheetIndex: number;
  sheetName: string;
  sheetJobType: string;
  sheetFlatNote: string;
  sheetHidden: boolean;
  rowKind: PriceBookRowKind;
  subsection: PriceBookSubsectionKey;
  name: string;
  nameEditable: boolean;
  unit: string;
  regular: number | null;
  ot1: number | null;
  ot2: number | null;
  hidden: boolean;
}

const ROW_KINDS: readonly PriceBookRowKind[] = ['equipment', 'crew', 'lineItem'];
const SUBSECTION_KEYS: readonly PriceBookSubsectionKey[] = ['equipment', 'labor', 'material', 'additional'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

export function readPriceBookMoney(value: unknown): number | null {
  return toMoneyAmount(value);
}

/** An overtime / usage tier a sheet does not charge is stored as 0: it is "not set" (a dash), never $0.00. */
function readTierRate(value: unknown): number | null {
  const amount = readPriceBookMoney(value);
  return amount === 0 ? null : amount;
}

function readRowKind(value: unknown): PriceBookRowKind {
  return ROW_KINDS.find((kind) => kind === value) ?? 'equipment';
}

function defaultSubsection(rowKind: PriceBookRowKind): PriceBookSubsectionKey {
  if (rowKind === 'crew') return 'labor';
  if (rowKind === 'lineItem') return 'additional';
  return 'equipment';
}

function readSubsection(value: unknown, rowKind: PriceBookRowKind): PriceBookSubsectionKey {
  return SUBSECTION_KEYS.find((key) => key === value) ?? defaultSubsection(rowKind);
}

export function parsePriceBookBuildRow(raw: unknown, position: number): PriceBookBuildRow | null {
  if (!isRecord(raw)) return null;
  const sheetId = readString(raw.sheet_id);
  if (!sheetId) return null;
  const rowKind = readRowKind(raw.row_kind);
  const sheetIndex = typeof raw.sheet_index === 'number' && Number.isFinite(raw.sheet_index) ? raw.sheet_index : Number.MAX_SAFE_INTEGER;
  return {
    id: readString(raw.id) || `${sheetId}:row:${position}`,
    sheetId,
    sheetIndex,
    sheetName: readString(raw.sheet_name) || sheetId,
    sheetJobType: readString(raw.sheet_job_type),
    sheetFlatNote: readString(raw.sheet_flat_note),
    sheetHidden: raw.sheet_hidden === true,
    rowKind,
    subsection: readSubsection(raw.subsection, rowKind),
    name: readString(raw.name),
    nameEditable: raw.name_editable === true,
    unit: readString(raw.unit),
    regular: readPriceBookMoney(raw.regular),
    ot1: readTierRate(raw.ot1),
    ot2: readTierRate(raw.ot2),
    hidden: raw.hidden === true
  };
}

export function parsePriceBookBuildRows(rows: readonly unknown[] | undefined): PriceBookBuildRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row, index) => {
    const parsed = parsePriceBookBuildRow(row, index);
    return parsed ? [parsed] : [];
  });
}
